import { FormEvent, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../services/api";

type SchoolClass = { id: number; name: string; available_spots: number };
type Receipt = {
  application_number: string;
  tracking_token: string;
  status: string;
  decision_notes?: string;
};

export default function PublicAdmissions() {
  const classes = useQuery({
    queryKey: ["public-admission-classes"],
    queryFn: async () =>
      (await api.get("/admissions/public/classes/")).data as SchoolClass[],
  });
  const [receipt, setReceipt] = useState<Receipt | null>(null),
    [error, setError] = useState(""),
    [tracking, setTracking] = useState<Receipt | null>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const d = new FormData(e.currentTarget);
    try {
      const r = await api.post(
        "/admissions/public/apply/",
        Object.fromEntries(d),
      );
      setReceipt(r.data);
    } catch {
      setError(
        "The application could not be submitted. Check all fields and try again.",
      );
    }
  };
  const track = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    try {
      const r = await api.get(
        `/admissions/public/track/${d.get("number")}/${d.get("token")}/`,
      );
      setTracking(r.data);
    } catch {
      setError("Application not found. Check the number and tracking token.");
    }
  };
  const answerOffer = async (response: "accept" | "decline") => {
    if (!tracking) return;
    try {
      const result = await api.post(`/admissions/public/track/${tracking.application_number}/${tracking.tracking_token}/respond/`, { response });
      setTracking(result.data);
    } catch { setError("The offer response could not be recorded."); }
  };
  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-bold">School admissions</h1>
          <p className="text-gray-600">
            Apply online or track an existing application.
          </p>
        </header>
        {error && (
          <div className="rounded bg-red-50 p-3 text-red-700">{error}</div>
        )}
        {receipt ? (
          <section className="card">
            <div className="card-content space-y-3">
              <h2 className="text-xl font-semibold text-green-700">
                Application submitted
              </h2>
              <p>
                Application number:{" "}
                <strong>{receipt.application_number}</strong>
              </p>
              <p>
                Tracking token:{" "}
                <strong className="break-all">{receipt.tracking_token}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Save both values securely. They are required to check the
                decision.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setReceipt(null)}
              >
                Submit another
              </button>
            </div>
          </section>
        ) : (
          <form onSubmit={submit} className="card">
            <div className="card-content grid gap-4 md:grid-cols-2">
              <h2 className="text-xl font-semibold md:col-span-2">
                New application
              </h2>
              <input
                required
                name="first_name"
                className="input"
                placeholder="Learner first name"
              />
              <input
                required
                name="last_name"
                className="input"
                placeholder="Learner last name"
              />
              <input
                required
                name="date_of_birth"
                type="date"
                className="input"
              />
              <select required name="gender" className="input">
                <option value="">Gender</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
              <select
                required
                name="class_applied_for"
                className="input md:col-span-2"
              >
                <option value="">Class applied for</option>
                {classes.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.available_spots} places
                  </option>
                ))}
              </select>
              <input
                required
                name="guardian_name"
                className="input"
                placeholder="Guardian name"
              />
              <input
                required
                name="guardian_phone"
                className="input"
                placeholder="Guardian phone"
              />
              <input
                required
                type="email"
                name="guardian_email"
                className="input"
                placeholder="Guardian email"
              />
              <input
                name="previous_school"
                className="input"
                placeholder="Previous school"
              />
              <textarea
                required
                name="address"
                className="input md:col-span-2"
                placeholder="Home address"
              />
              <textarea
                name="notes"
                className="input md:col-span-2"
                placeholder="Additional information"
              />
              <button className="btn btn-primary md:col-span-2">
                Submit application
              </button>
            </div>
          </form>
        )}
        <form onSubmit={track} className="card">
          <div className="card-content grid gap-3 md:grid-cols-3">
            <h2 className="text-xl font-semibold md:col-span-3">
              Track application
            </h2>
            <input
              required
              name="number"
              className="input"
              placeholder="Application number"
            />
            <input
              required
              name="token"
              className="input"
              placeholder="Tracking token"
            />
            <button className="btn btn-secondary">Check status</button>
            {tracking && (
              <div className="md:col-span-3 rounded bg-blue-50 p-4">
                <strong>{tracking.application_number}</strong>:{" "}
                <span className="capitalize">
                  {tracking.status.replace("_", " ")}
                </span>
                {tracking.decision_notes && <p className="mt-2 text-sm">{tracking.decision_notes}</p>}
                {tracking.status === "offered" && <div className="mt-3 flex gap-2"><button type="button" onClick={()=>answerOffer("accept")} className="btn btn-primary">Accept offer</button><button type="button" onClick={()=>answerOffer("decline")} className="btn btn-outline">Decline offer</button></div>}
              </div>
            )}
          </div>
        </form>
      </div>
    </main>
  );
}
